import { apiCreate, apiFetch, type RequestOptions } from './client'
import { relatedIds, relatedResource } from './documents'
import {
  type Document,
  type PaymentMethodAttributes,
  type PaymentMethodStatus,
  type Resource,
  SHIPPING_ADDRESSES_TYPE,
  type ShippingAddressAttributes,
  SUBSCRIPTIONS_TYPE,
} from './types'

/**
 * The purchaser's address book at Recharge, seen through one of their
 * subscriptions. Addresses are shared — every child's kit can ship to one —
 * so they are added and chosen, never edited, and each is charged to a
 * payment method that Shopify keeps and the portal only ever reads.
 */

/** The card, or wallet, an address is charged to. */
export interface PaymentMethod {
  id: string
  /** Such as CREDIT_CARD, PAYPAL or APPLE_PAY, as Recharge names them. */
  type: string
  brand: string | null
  last4: string | null
  /** The last day the card can be charged, as YYYY-MM-DD; null for a method that does not expire. */
  expiresOn: string | null
  /** Whether Recharge can charge it. */
  status: PaymentMethodStatus | null
}

export interface ShippingAddress {
  id: string
  firstName: string
  lastName: string
  address1: string
  address2: string | null
  city: string
  province: string
  zip: string
  phone: string | null
  /** Our subscriptions that ship to this address. */
  subscriptionIds: string[]
  /** What the address is charged to, or null when Recharge has nothing for it yet. */
  paymentMethod: PaymentMethod | null
}

/**
 * What adding an address takes. The shop ships within the United States
 * only, so the country is not asked; a state is its full name, as Recharge
 * wants it.
 */
export interface NewShippingAddress {
  firstName: string
  lastName: string
  address1: string
  address2: string
  city: string
  province: string
  zip: string
  phone: string
}

export type AddressField = keyof NewShippingAddress

const ATTRIBUTE_NAMES = {
  firstName: 'first_name',
  lastName: 'last_name',
  address1: 'address1',
  address2: 'address2',
  city: 'city',
  province: 'province',
  zip: 'zip',
  phone: 'phone',
} as const satisfies Record<AddressField, string>

/** The field an API attribute belongs to, so a refusal can land beside it. */
export function addressFieldFor(attribute: string): AddressField | undefined {
  return (Object.keys(ATTRIBUTE_NAMES) as AddressField[]).find(
    (field) => ATTRIBUTE_NAMES[field] === attribute,
  )
}

/** The address in the API's naming, without the parts left blank. */
export function toShippingAddressAttributes(address: NewShippingAddress): Record<string, string> {
  const attributes: Record<string, string> = { country_code: 'US' }
  for (const field of Object.keys(ATTRIBUTE_NAMES) as AddressField[]) {
    const value = address[field].trim()
    if (value !== '') attributes[ATTRIBUTE_NAMES[field]] = value
  }
  return attributes
}

export function toPaymentMethod(resource: Resource): PaymentMethod {
  const attributes = resource.attributes as PaymentMethodAttributes | undefined
  if (!attributes) throw new Error(`Payment method ${resource.id} came back without attributes`)

  return {
    id: resource.id,
    type: attributes.payment_type,
    brand: attributes.brand ?? null,
    last4: attributes.last4 ?? null,
    expiresOn: attributes.expires_on ?? null,
    status: attributes.status ?? null,
  }
}

export function toShippingAddress(
  document: Document<unknown>,
  resource: Resource,
): ShippingAddress {
  const attributes = resource.attributes as ShippingAddressAttributes | undefined
  if (!attributes) throw new Error(`Shipping address ${resource.id} came back without attributes`)

  const paymentMethod = relatedResource(document, resource, 'payment_method')

  return {
    id: resource.id,
    firstName: attributes.first_name,
    lastName: attributes.last_name,
    address1: attributes.address1,
    address2: attributes.address2 ?? null,
    city: attributes.city,
    province: attributes.province,
    zip: attributes.zip,
    phone: attributes.phone ?? null,
    subscriptionIds: relatedIds(resource, 'subscriptions'),
    paymentMethod: paymentMethod ? toPaymentMethod(paymentMethod) : null,
  }
}

/** The address book of the purchaser behind a subscription. */
export async function fetchShippingAddresses(
  subscriptionId: string,
  options: RequestOptions = {},
): Promise<ShippingAddress[]> {
  const document = await apiFetch<Document<Resource[]>>(
    `/subscriptions/${subscriptionId}/shipping_addresses`,
    options,
  )
  return document.data.map((resource) => toShippingAddress(document, resource))
}

/** Add an address to the book and ship the subscription to it from now on. */
export async function createShippingAddress(
  subscriptionId: string,
  address: NewShippingAddress,
  options: RequestOptions = {},
): Promise<ShippingAddress> {
  const document = await apiCreate<Document<Resource>>(
    '/shipping_addresses',
    {
      type: SHIPPING_ADDRESSES_TYPE,
      attributes: toShippingAddressAttributes(address),
      relationships: { subscription: { data: { type: SUBSCRIPTIONS_TYPE, id: subscriptionId } } },
    },
    options,
  )
  return toShippingAddress(document, document.data)
}
